import { api } from './client'
import type { LedgerSourceClaim } from './ledger-source-claim'

// ============================================================================
// Sổ lấy hương (#M-16) — ghi chép của xưởng, tách khỏi phiếu kho.
//
// Người lấy hương ghi từng mẻ; kế toán bấm một nút để mở màn tạo phiếu nhập với
// dữ liệu điền sẵn. Sổ KHÔNG đụng tồn kho: tồn chỉ đổi khi phiếu được duyệt.
// ============================================================================

const BASE = '/scenting-lots/log-entries'

/** draft = chưa lập phiếu · synced = khớp kho · diverged = sửa sau khi có phiếu. */
export type ScentingLogSyncState = 'draft' | 'synced' | 'diverged'

export interface ScentingLogEntrySource {
    id: string
    entry_id: string
    source_sorting_lot_id: string
    quantity: number
    position: number
    /** Mã lô — CHỈ để điền sang phiếu; giao diện sổ hiện "Loại — ngày tạo". */
    source_lot_code?: string
    source_grade_name?: string
    source_created_at?: string
    source_available: number
    /** kg khả dụng của lô lúc chọn, lưu theo từng dòng lô (M-72). Mẻ ghi
     *  trước M-72 trả kg xuất; server cũ chưa trả trường này. */
    source_available_snapshot?: number
}

export interface ScentingLogEntryOutput {
    id: string
    entry_id: string
    quantity: number
    lot_code?: string | null
    /** Có sau khi phiếu được duyệt và lô thật đã sinh ra. */
    scenting_lot_id?: string | null
    scenting_lot_code?: string
    position: number
    /**
     * M-74: dòng loại thứ mấy của mẻ, lô chính hay lô chè tấm, và loại của lô.
     * Server luôn trả; để optional cho dữ liệu dựng tay của bản trước.
     */
    line_no?: number
    kind?: 'main' | 'broken'
    grade_id?: string
    grade_name?: string
}

export interface ScentingLogEntry {
    /** Người làm mẻ (#392). Mẻ ghi trước #392 để trống. */
    employee_id?: string | null
    employee_name?: string
    id: string
    scenting_date: string
    grade_id: string
    grade_name?: string
    notes?: string | null
    note_id?: string | null
    note_code?: string
    note_status?: '' | 'pending' | 'approved' | 'rejected' | 'cancelled'
    sync_state: ScentingLogSyncState
    /** Ba trường dưới đây để giao diện nói rõ vì sao dòng bị khoá TRƯỚC khi bấm. */
    can_edit: boolean
    can_delete: boolean
    lock_reason?: string
    /** Lý do chưa bấm được "Tạo điều chỉnh" — hiện TRƯỚC khi bấm, không đợi lỗi. */
    adjust_block_reason?: string
    created_by_id?: string | null
    created_by_name?: string
    created_at: string
    updated_at: string
    sources: ScentingLogEntrySource[]
    outputs: ScentingLogEntryOutput[]
}

/** Một dòng loại của mẻ (M-74): loại lấy hương, lô chính (≥2 = tách lô) và
 *  các lô chè tấm. id có = dòng đã tồn tại (giữ liên kết lô). */
export interface ScentingLogLineInput {
    grade_id: string
    outputs: Array<{ id?: string; quantity: number; lot_code?: string }>
    broken: Array<{
        id?: string
        grade_id: string
        quantity: number
        lot_code?: string
    }>
}

export interface ScentingLogEntryInput {
    /** Nhân sự thực sự làm mẻ này (#392) — bắt buộc. */
    employee_id: string
    scenting_date: string
    /** Hình dạng trước M-74 (một loại mỗi mẻ) — dùng `lines` thay thế. */
    grade_id?: string
    notes?: string
    sources: Array<{
        source_lot_id: string
        quantity: number
        /** kg khả dụng người ghi đã thấy lúc chọn lô (M-72). */
        available_snapshot?: number
    }>
    /** Hình dạng trước M-74 — id có = dòng đã tồn tại; không có = dòng mới. */
    outputs?: Array<{ id?: string; quantity: number; lot_code?: string }>
    /** Các dòng loại của mẻ (M-74). */
    lines?: ScentingLogLineInput[]
}

export interface ListScentingLogParams {
    date_from?: string
    date_to?: string
    sync_state?: ScentingLogSyncState
    created_by?: string
    page?: number
    per_page?: number
}

/** Một lô lệch. lot_id rỗng nghĩa là lô mới thêm sau, chưa tồn tại trong kho. */
export interface ScentingLogLotDelta {
    LotID: string
    Quantity: number
    LotCode: string
    /** Loại của lô mới (M-74); chỉ có ở nhóm ScentingNewLots. */
    GradeID?: string
}

/** Năm nhóm chênh lệch, mỗi nhóm ứng với một loại phiếu điều chỉnh sẽ sinh ra. */
export interface ScentingLogDivergence {
    ScentingIncrease: ScentingLogLotDelta[] | null
    ScentingDecrease: ScentingLogLotDelta[] | null
    ScentingNewLots: ScentingLogLotDelta[] | null
    SortingTakeMore: ScentingLogLotDelta[] | null
    SortingGiveBack: ScentingLogLotDelta[] | null
}

export type AdjustedNoteKind =
    'scenting_in' | 'scenting_out' | 'sorting_in' | 'sorting_out'

export interface AdjustedNote {
    id: string
    code: string
    kind: AdjustedNoteKind
    quantity: number
}

export const scentingLogApi = {
    list: (params?: ListScentingLogParams) =>
        api.get<ScentingLogEntry[]>(BASE, { params }),
    get: (id: string) => api.get<ScentingLogEntry>(`${BASE}/${id}`),
    divergence: (id: string) =>
        api.get<ScentingLogDivergence>(`${BASE}/${id}/divergence`),
    /** M-140: phần lô nguồn đang bị các mẻ chưa vào kho KHÁC giữ — ô chọn lô
     *  trừ sẵn. `excludeEntryId` = mẻ đang sửa. */
    sourceClaims: (excludeEntryId?: string) =>
        api.get<LedgerSourceClaim[]>(`${BASE}/source-claims`, {
            params: excludeEntryId
                ? { exclude_entry_id: excludeEntryId }
                : undefined,
        }),
    create: (req: ScentingLogEntryInput) =>
        api.post<ScentingLogEntry>(BASE, req),
    update: (id: string, req: ScentingLogEntryInput) =>
        api.put<ScentingLogEntry>(`${BASE}/${id}`, req),
    remove: (id: string) => api.delete<void>(`${BASE}/${id}`),
    adjust: (id: string) => api.post<AdjustedNote[]>(`${BASE}/${id}/adjust`),
}
